/**
 * DV-TEMP-001: Vertical Timeline Component with Stage Groupings
 * Replaces flat event list with visual workflow progression
 */

import React, { useState, useCallback, useMemo } from 'react';
import { List as FixedSizeList } from 'react-window';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Hash,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle,
  Loader2,
  MoreHorizontal,
  // Play, // Removed unused import
  Pause,
  ExternalLink,
  Activity
} from 'lucide-react';
import { cn } from '@/utils/cn';
import type { TimelineEvent } from '@/types/execution-events';
import { STAGE_DISPLAY_CONFIG, EVENT_TYPE_DISPLAY_CONFIG } from '@/types/execution-events';
import { TemporalLinks } from './TemporalLinks';

export interface VerticalTimelineProps {
  events: TimelineEvent[];
  eventsByStage: Map<string, TimelineEvent[]>;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  className?: string;
  'data-testid'?: string;
  // DV-TEMP-004: Virtualization options
  enableVirtualization?: boolean;
  virtualListHeight?: number;
  itemHeight?: number;
}

interface StageTimelineProps {
  stage: string;
  events: TimelineEvent[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  testId?: string;
  // DV-TEMP-004: Virtualization support
  enableVirtualization?: boolean;
  virtualListHeight?: number;
  itemHeight?: number;
}

interface EventRowProps {
  event: TimelineEvent;
  isExpanded: boolean;
  onToggleExpand: () => void;
  testId?: string;
}

// DV-TEMP-004: Virtualization constants
const DEFAULT_ITEM_HEIGHT = 140; // Height per event row
const DEFAULT_VIRTUAL_HEIGHT = 600; // Default virtual list height
const VIRTUALIZATION_THRESHOLD = 50; // Enable virtualization above this many events

// Virtual row component for react-window
interface VirtualListData {
  events: TimelineEvent[];
  expandedEvents: Set<string>;
  onToggleExpand: (eventId: string) => void;
  testId?: string;
}

interface VirtualEventRowProps {
  index: number;
  style: React.CSSProperties;
  data: VirtualListData;
  isScrolling?: boolean;
}

function VirtualEventRow({ index, style, data }: VirtualEventRowProps) {
  const { events, expandedEvents, onToggleExpand, testId } = data;
  const event = events[index];

  if (!event) return null;

  return (
    <div style={style}>
      <EventRow
        event={event}
        isExpanded={expandedEvents.has(event.id)}
        onToggleExpand={() => onToggleExpand(event.id)}
        testId={testId ? `${testId}-event-${index}` : undefined}
      />
    </div>
  );
}

// Stage progression order based on the actual pipeline
const STAGE_ORDER = [
  'script', 'vision', 'storyboard', 'shots', 'continuity', 'elements',
  'frames', 'videos', 'music', 'music_master', 'music_sidechain',
  'export_preview', 'qc', 'assemble', 'publish'
];

// Severity icon mapping
const SeverityIcon = ({ severity }: { severity: TimelineEvent['severity'] }) => {
  const iconProps = { className: "w-4 h-4" };

  switch (severity) {
    case 'success':
      return <CheckCircle {...iconProps} style={{ color: 'var(--status-success)' }} />;
    case 'error':
      return <AlertCircle {...iconProps} style={{ color: 'var(--status-error)' }} />;
    case 'warning':
      return <AlertTriangle {...iconProps} style={{ color: 'var(--status-warning)' }} />;
    default:
      return <Info {...iconProps} style={{ color: 'var(--fg-muted)' }} />;
  }
};

// Stage status calculation
function getStageStatus(events: TimelineEvent[]): 'pending' | 'running' | 'completed' | 'failed' {
  if (events.length === 0) return 'pending';

  const hasError = events.some(e => e.severity === 'error');
  if (hasError) return 'failed';

  const hasCompleted = events.some(e => e.event_type.includes('completed'));
  if (hasCompleted) return 'completed';

  const hasStarted = events.some(e => e.event_type.includes('started'));
  if (hasStarted) return 'running';

  return 'pending';
}

// Stage status icon
const StageStatusIcon = ({ status }: { status: ReturnType<typeof getStageStatus> }) => {
  const iconProps = { className: "w-5 h-5" };

  switch (status) {
    case 'completed':
      return <CheckCircle {...iconProps} style={{ color: 'var(--status-success)' }} />;
    case 'failed':
      return <AlertCircle {...iconProps} style={{ color: 'var(--status-error)' }} />;
    case 'running':
      return <Activity {...iconProps} style={{ color: 'var(--accent)' }} className="animate-pulse" />;
    default:
      return <Pause {...iconProps} style={{ color: 'var(--fg-muted)' }} />;
  }
};

// Individual event row component
function EventRow({ event, isExpanded, onToggleExpand, testId }: EventRowProps) {
  const eventConfig = EVENT_TYPE_DISPLAY_CONFIG[event.event_type];
  const hasPayload = event.isExpandable && Object.keys(event.payload || {}).length > 0;

  return (
    <div
      className={cn(
        "group relative flex gap-3 p-3 rounded border transition-all",
        isExpanded && "ring-1",
        hasPayload && "cursor-pointer"
      )}
      style={{
        backgroundColor: 'var(--bg-elevated)',
        borderColor: 'var(--border-subtle)',
        ['--ring-color' as any]: 'var(--border-focus)',
      }}
      onClick={hasPayload ? onToggleExpand : undefined}
      data-testid={testId}
    >
      {/* Event Type Icon */}
      <div className="flex-shrink-0 mt-0.5">
        <SeverityIcon severity={event.severity} />
      </div>

      {/* Event Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            {/* Event Title */}
            <div className="flex items-center gap-2 mb-1">
              <span
                className="font-medium text-sm"
                style={{ color: 'var(--fg-primary)' }}
              >
                {event.displayTitle}
              </span>
              {eventConfig && (
                <span className="text-xs">{eventConfig.icon}</span>
              )}
              {event.attemptNumber && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: 'var(--bg-surface)',
                    color: 'var(--fg-muted)'
                  }}
                >
                  #{event.attemptNumber}
                </span>
              )}
            </div>

            {/* Event Description */}
            {event.displayDescription && (
              <div
                className="text-xs mb-2"
                style={{ color: 'var(--fg-muted)' }}
              >
                {event.displayDescription}
              </div>
            )}

            {/* Artifacts */}
            {event.artifactRefs.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {event.artifactRefs.slice(0, 3).map((artifact, idx) => (
                  <a
                    key={idx}
                    href={artifact.publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors"
                    style={{
                      backgroundColor: 'var(--bg-surface)',
                      borderColor: 'var(--border-default)',
                      color: 'var(--accent)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {artifact.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ))}
                {event.artifactRefs.length > 3 && (
                  <span
                    className="px-2 py-1 rounded text-xs"
                    style={{
                      backgroundColor: 'var(--bg-surface)',
                      color: 'var(--fg-muted)'
                    }}
                  >
                    +{event.artifactRefs.length - 3} more
                  </span>
                )}
              </div>
            )}

            {/* Timestamp and Correlation */}
            <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--fg-muted)' }}>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(event.timestamp).toLocaleTimeString()}
              </div>
              {event.correlation_id && (
                <div className="flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  <span className="font-mono">{event.correlation_id.slice(0, 8)}</span>
                </div>
              )}
            </div>

            {/* DV-TEMP-004: Compact Temporal Links for events with workflow_run_id */}
            {event.workflow_run_id && (
              <div className="mt-2">
                <TemporalLinks
                  executionId={event.execution_id}
                  workflowRunId={event.workflow_run_id}
                  compact={true}
                />
              </div>
            )}
          </div>

          {/* Expand Toggle */}
          {hasPayload && (
            <button
              className="flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: 'var(--fg-muted)' }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Expanded Payload */}
        {isExpanded && hasPayload && (
          <div
            className="mt-3 p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div className="text-xs font-mono" style={{ color: 'var(--fg-primary)' }}>
              <pre className="whitespace-pre-wrap overflow-auto max-h-40">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Stage timeline section component
function StageTimeline({
  stage,
  events,
  isExpanded,
  onToggleExpand,
  testId,
  enableVirtualization = true,
  virtualListHeight = DEFAULT_VIRTUAL_HEIGHT,
  itemHeight = DEFAULT_ITEM_HEIGHT
}: StageTimelineProps) {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const stageConfig = STAGE_DISPLAY_CONFIG[stage as keyof typeof STAGE_DISPLAY_CONFIG];
  const stageStatus = getStageStatus(events);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [events]);

  // DV-TEMP-004: Determine if we should use virtualization
  const shouldVirtualize = enableVirtualization && sortedEvents.length > VIRTUALIZATION_THRESHOLD;

  // Virtual list data for react-window
  const virtualListData = useMemo(() => ({
    events: sortedEvents,
    expandedEvents,
    onToggleExpand: (eventId: string) => {
      setExpandedEvents(prev => {
        const newSet = new Set(prev);
        if (newSet.has(eventId)) {
          newSet.delete(eventId);
        } else {
          newSet.add(eventId);
        }
        return newSet;
      });
    },
    testId
  }), [sortedEvents, expandedEvents, testId]);

  const handleEventToggle = useCallback((eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  if (!stageConfig) {
    return null; // Skip unknown stages
  }

  return (
    <div className="relative" data-testid={testId}>
      {/* Stage Header */}
      <div
        className="sticky top-0 z-10 flex items-center gap-3 p-3 rounded-t border cursor-pointer transition-colors"
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--border-default)',
          ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
        }}
        onClick={onToggleExpand}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
        }}
      >
        {/* Stage Status */}
        <StageStatusIcon status={stageStatus} />

        {/* Stage Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-lg">{stageConfig.icon}</span>
            <span
              className="font-medium"
              style={{ color: 'var(--fg-primary)' }}
            >
              {stageConfig.label}
            </span>
            <span
              className="text-sm px-2 py-0.5 rounded"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                color: 'var(--fg-muted)'
              }}
            >
              {events.length} events
            </span>
          </div>
          <div
            className="text-xs mt-0.5"
            style={{ color: 'var(--fg-muted)' }}
          >
            Status: {stageStatus}
          </div>
        </div>

        {/* Expand Toggle */}
        <ChevronDown
          className={cn("w-5 h-5 transition-transform", !isExpanded && "-rotate-90")}
          style={{ color: 'var(--fg-muted)' }}
        />
      </div>

      {/* Stage Events */}
      {isExpanded && (
        <div
          className="border-l-2 border-b border-r rounded-b"
          style={{
            borderColor: 'var(--border-default)',
            marginLeft: '1rem'
          }}
        >
          {/* DV-TEMP-004: Conditional virtualization based on event count */}
          {shouldVirtualize ? (
            <div className="p-3">
              <div
                className="text-xs mb-2 text-center"
                style={{ color: 'var(--fg-muted)' }}
              >
                📊 Virtualized view ({sortedEvents.length} events)
              </div>
              {(FixedSizeList as any)({
                height: virtualListHeight,
                itemCount: sortedEvents.length,
                itemSize: itemHeight,
                itemData: virtualListData,
                className: "timeline-virtual-list",
                children: VirtualEventRow as any
              })}
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {sortedEvents.map((event, idx) => (
                <EventRow
                  key={event.id}
                  event={event}
                  isExpanded={expandedEvents.has(event.id)}
                  onToggleExpand={() => handleEventToggle(event.id)}
                  testId={`${testId}-event-${idx}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main vertical timeline component
export function VerticalTimeline({
  events,
  eventsByStage,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  className,
  'data-testid': testId = 'vertical-timeline',
  // DV-TEMP-004: Virtualization options
  enableVirtualization = true,
  virtualListHeight = DEFAULT_VIRTUAL_HEIGHT,
  itemHeight = DEFAULT_ITEM_HEIGHT
}: VerticalTimelineProps) {
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set(['script', 'vision', 'storyboard']));

  // Sort stages by pipeline order
  const sortedStages = useMemo(() => {
    const stagesWithEvents = Array.from(eventsByStage.keys());
    return STAGE_ORDER.filter(stage => stagesWithEvents.includes(stage));
  }, [eventsByStage]);

  const handleStageToggle = useCallback((stage: string) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(stage)) {
        next.delete(stage);
      } else {
        next.add(stage);
      }
      return next;
    });
  }, []);

  // Pipeline-level events (events without a specific stage)
  const pipelineEvents = useMemo(() => {
    return events.filter(event => !event.stage);
  }, [events]);

  if (isLoading && events.length === 0) {
    return (
      <div
        className={cn("flex items-center justify-center p-8", className)}
        data-testid={`${testId}-loading`}
      >
        <div className="flex items-center gap-2" style={{ color: 'var(--fg-muted)' }}>
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading timeline events...
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div
        className={cn("text-center p-8", className)}
        style={{ color: 'var(--fg-muted)' }}
        data-testid={`${testId}-empty`}
      >
        <Activity className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fg-muted)' }} />
        <div className="text-sm font-medium mb-1">No Events Found</div>
        <div className="text-xs">
          Events will appear here as the pipeline executes. Try triggering a test pipeline or adjusting your filters.
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)} data-testid={testId}>
      {/* Pipeline Events */}
      {pipelineEvents.length > 0 && (
        <StageTimeline
          stage="pipeline"
          events={pipelineEvents}
          isExpanded={expandedStages.has('pipeline')}
          onToggleExpand={() => handleStageToggle('pipeline')}
          testId={`${testId}-pipeline`}
          enableVirtualization={enableVirtualization}
          virtualListHeight={virtualListHeight}
          itemHeight={itemHeight}
        />
      )}

      {/* Stage Events */}
      {sortedStages.map(stage => {
        const stageEvents = eventsByStage.get(stage) || [];
        return (
          <StageTimeline
            key={stage}
            stage={stage}
            events={stageEvents}
            isExpanded={expandedStages.has(stage)}
            onToggleExpand={() => handleStageToggle(stage)}
            testId={`${testId}-${stage}`}
            enableVirtualization={enableVirtualization}
            virtualListHeight={virtualListHeight}
            itemHeight={itemHeight}
          />
        );
      })}

      {/* Load More Button */}
      {hasNextPage && (
        <div className="flex justify-center p-4">
          <button
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="flex items-center gap-2 px-4 py-2 rounded border transition-colors focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              color: 'var(--fg-primary)',
              ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
            }}
            data-testid={`${testId}-load-more`}
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <MoreHorizontal className="w-4 h-4" />
                Load More Events
              </>
            )}
          </button>
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && events.length > 0 && (
        <div className="flex justify-center p-2">
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--fg-muted)' }}>
            <Loader2 className="w-4 h-4 animate-spin" />
            Refreshing events...
          </div>
        </div>
      )}
    </div>
  );
}